import { useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import { jwtDecode } from "jwt-decode";
import { useNavigate, useParams } from "react-router-dom";
import { signInWithCustomToken, signOut } from "firebase/auth";

import { FullPageLoading } from "@/components/full-page-loading";
import { auth } from "@/firebase";
import {
  SKED_AUTH,
  type SkedAuthData,
  type SkedAuthVariables,
} from "@/graphql";
import { useDocumentTitle } from "@/hooks/use-document-title";

export function SkedAuthPage() {
  useDocumentTitle("Signing in");
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const ranRef = useRef(false);

  const [skedAuth] = useMutation<SkedAuthData, SkedAuthVariables>(SKED_AUTH);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    async function run() {
      if (!token) {
        setError("Missing authentication token.");
        return;
      }
      try {
        const { data } = await skedAuth({ variables: { token } });
        const firebaseToken = data?.skedAuth.token;
        if (!firebaseToken) {
          throw new Error("No firebase token returned");
        }
        await signOut(auth);
        await signInWithCustomToken(auth, firebaseToken);

        // Store Intercom identifiers so the widget can verify the user.
        try {
          const decoded = jwtDecode<{
            userId?: string;
            intercom?: { web?: string };
          }>(token);
          if (decoded.intercom?.web) {
            window.localStorage.setItem("intercom-hash", decoded.intercom.web);
          }
          if (decoded.userId) {
            window.localStorage.setItem("intercom-id", decoded.userId);
          }
        } catch {
          // Non-critical — Intercom will still work without verification.
        }

        navigate("/", { replace: true });
      } catch (err) {
        console.error("SkedAuth failed", err);
        setError("Sign-in failed. Please try again from Sked Social.");
      }
    }

    void run();
  }, [token, skedAuth, navigate]);

  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-8 py-16 text-center">
        <h1 className="font-gotham text-2xl font-semibold text-neutral-900">
          {error}
        </h1>
      </div>
    );
  }

  return <FullPageLoading label="Signing you in with Sked Social…" />;
}
